/**
 * Dev Console — Partner Companies: what only Fias can give a Company under its
 * own name (ADR-023), one row per Company with one chip per grant.
 *
 *   company_branding — the Company's brand (logo, colours, name) on every linked
 *                      workspace's Document Sign pages and emails.
 *   company_email    — send email as the Company, from the exact domains named
 *                      in the grant.
 *   company_web      — Company portals (ADR-024): the Company's own sign-in
 *                      front door at subdomains of the exact domains named in
 *                      the grant. Needs branding (a portal is branded).
 *
 * One table, but the grants stay SEPARATE: each has its own row server-side,
 * its own reason, its own audit event, and is given or taken away on its own.
 * Neither requires the other. The table is driven by `GRANTS`, so another
 * Fias-granted capability is one entry there (plus its server key).
 *
 * A Company cannot grant any of these to itself: this is the only way in.
 * Every change needs a reason and is audited server-side (`requireAdmin`). A
 * chip never toggles on click — it opens a dialog that says what the change
 * does, because revoking email stops a partner's mail at once.
 *
 * Taking something away never deletes the Company's work: a revoked brand is
 * kept and returns if granted again; a withdrawn email domain stops sending
 * now and is retired only after the offboarding grace.
 */

import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus } from 'lucide-react';
import {
  api,
  ApiError,
  type AdminCompanySearchResultDto,
  type CompanyEntitlementGrantDto,
  type CompanyEntitlementKey,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';

/** Mirrors COMPANY_EMAIL_MAX_APPROVED_DOMAINS in @fias/db-types. */
const MAX_DOMAINS = 10;
/** Mirrors COMPANY_WEB_MAX_DOMAINS_CEILING in @fias/db-types. */
const MAX_WEB_DOMAINS = 5;

interface GrantCopy {
  /** The chip's label. */
  label: string;
  /** Whether the grant names approved domains (company_email, company_web). */
  domains: boolean;
  /** The domains field's label, when `domains`. */
  domainsLabel?: string;
  /** What removing approved domains does, when `domains`. */
  removedDomainsNote?: (removed: string[]) => string;
  /** company_web: domain purchases and the domain count (ADR-024 rev 4). */
  portalSettings?: boolean;
  grantDescription: string;
  revokeDescription: string;
}

/** One entry per Fias-granted capability, in chip order. */
export const GRANTS: Record<CompanyEntitlementKey, GrantCopy> = {
  company_branding: {
    label: 'Branding',
    domains: false,
    grantDescription:
      'The Company’s owners can set its logo, colours and name, and every workspace it links shows them on Document Sign pages and emails.',
    revokeDescription:
      'Linked workspaces stop showing the Company’s brand at once and show the Fias default instead. The Company’s saved brand is kept, and returns if you grant again.',
  },
  company_email: {
    label: 'Email',
    domains: true,
    domainsLabel: 'Approved email domains',
    removedDomainsNote: (removed) =>
      `Removing ${removed.join(', ')}: mail from ${removed.length === 1 ? 'it stops' : 'them stops'} now, and ${removed.length === 1 ? 'it is' : 'they are'} retired after 30 days.`,
    grantDescription:
      'The Company can add only these domains, and must still prove each one with DNS records. Approve only after checking it controls them.',
    revokeDescription:
      'All of this Company’s own-domain email stops now: arches are refused and Document Sign falls back to Fias. Its domains are retired after 30 days unless you grant again first.',
  },
  company_web: {
    label: 'Web',
    domains: true,
    portalSettings: true,
    domainsLabel: 'Approved web domains',
    removedDomainsNote: (removed) =>
      `Removing ${removed.join(', ')}: every portal on ${removed.length === 1 ? 'it' : 'them'} (moved or delegated) stops serving now and its users are signed out there.`,
    grantDescription:
      'Portals live only on domains Fias manages. Approved domains are ones the Company may move to Fias or delegate a subdomain of — approve only a domain you have confirmed is the partner’s. Domains it buys through Fias need no approval. Requires Branding.',
    revokeDescription:
      'Every portal under a removed domain stops serving at once and its users are signed out there. The portals themselves stay until the Company removes them.',
  },
};

const GRANT_KEYS = Object.keys(GRANTS) as CompanyEntitlementKey[];

export interface PartnerRow {
  companyId: string;
  companyName: string;
  grants: Partial<Record<CompanyEntitlementKey, CompanyEntitlementGrantDto>>;
}

/** Fold the per-grant listings into one row per Company, sorted by name. */
export function mergePartnerRows(
  lists: Array<{ key: CompanyEntitlementKey; grants: CompanyEntitlementGrantDto[] }>,
): PartnerRow[] {
  const rows = new Map<string, PartnerRow>();
  for (const { key, grants } of lists) {
    for (const g of grants) {
      const row = rows.get(g.companyId) ?? {
        companyId: g.companyId,
        companyName: g.companyName ?? g.companyId,
        grants: {},
      };
      row.grants[key] = g;
      rows.set(g.companyId, row);
    }
  }
  return [...rows.values()].sort((a, b) => a.companyName.localeCompare(b.companyName));
}

/** One domain per line or comma-separated; lower-cased, de-duplicated. */
export function parseDomains(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message.replace(/^[A-Z_]+:\s*/, '');
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}

type Editing =
  | { kind: 'add' }
  | { kind: 'grant'; row: PartnerRow; key: CompanyEntitlementKey }
  | { kind: 'manage'; row: PartnerRow; key: CompanyEntitlementKey }
  | { kind: 'revoke'; row: PartnerRow; key: CompanyEntitlementKey };

export function PartnerCompanies() {
  const qc = useQueryClient();
  const lists = useQueries({
    queries: GRANT_KEYS.map((key) => ({
      queryKey: queryKeys.adminCompanies.grants(key),
      queryFn: () => api.adminListCompanyGrants(key),
    })),
  });
  const [editing, setEditing] = useState<Editing | null>(null);

  const loading = lists.some((l) => l.isLoading);
  // A partial table would read as "this Company has no email" — show the
  // failure instead of whatever did load.
  const failed = lists.find((l) => l.isError);
  const rows = mergePartnerRows(
    GRANT_KEYS.map((key, i) => ({ key, grants: lists[i]?.data?.grants ?? [] })),
  );

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle>Partner Companies</CardTitle>
            <CardDescription>
              What a Company may do under its own name. Each is granted separately, with its own
              reason; neither requires the other.
            </CardDescription>
          </div>
          <Button onClick={() => setEditing({ kind: 'add' })}>Add a Company</Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : failed ? (
          <div className="flex flex-wrap items-center gap-2">
            <p role="alert" className="text-sm text-destructive">
              Could not load partner Companies. {errorText(failed.error)}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => lists.forEach((l) => void l.refetch())}
            >
              Try again
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No partner Companies yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((row) => (
              <li key={row.companyId} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1 font-medium text-foreground">{row.companyName}</div>
                <div className="flex flex-wrap gap-2">
                  {GRANT_KEYS.map((key) => (
                    <GrantChip
                      key={key}
                      grantKey={key}
                      row={row}
                      onClick={() =>
                        setEditing({ kind: row.grants[key] ? 'manage' : 'grant', row, key })
                      }
                    />
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {editing?.kind === 'add' && (
        <AddCompanyDialog
          rows={rows}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: queryKeys.adminCompanies.all });
          }}
        />
      )}
      {editing && editing.kind !== 'add' && (
        <GrantDialog
          key={`${editing.kind}:${editing.row.companyId}:${editing.key}`}
          editing={editing}
          onRevoke={() => setEditing({ ...editing, kind: 'revoke' })}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: queryKeys.adminCompanies.all });
          }}
        />
      )}
    </Card>
  );
}

function GrantChip({
  grantKey,
  row,
  onClick,
}: {
  grantKey: CompanyEntitlementKey;
  row: PartnerRow;
  onClick: () => void;
}) {
  const copy = GRANTS[grantKey];
  const grant = row.grants[grantKey];
  const domains = grant?.approvedDomains ?? [];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        grant
          ? `Manage ${copy.label} for ${row.companyName}`
          : `Allow ${copy.label} for ${row.companyName}`
      }
      title={
        grant
          ? `Granted ${new Date(grant.grantedAt).toLocaleDateString()} — ${grant.reason}`
          : undefined
      }
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        grant
          ? 'border-primary/40 bg-primary/10 text-foreground hover:bg-primary/20'
          : 'border-dashed border-border text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {grant ? (
        <Check className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <Plus className="h-3.5 w-3.5 shrink-0" />
      )}
      <span>{copy.label}</span>
      {copy.domains && domains.length > 0 && (
        <span className="truncate text-muted-foreground">
          · {domains[0]}
          {domains.length > 1 ? ` +${domains.length - 1}` : ''}
        </span>
      )}
    </button>
  );
}

/** Grant one capability to a listed Company, manage a held one, or revoke it. */
function GrantDialog({
  editing,
  onRevoke,
  onClose,
  onSaved,
}: {
  editing: Exclude<Editing, { kind: 'add' }>;
  onRevoke: () => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { row, key } = editing;
  const copy = GRANTS[key];
  const existing = row.grants[key] ?? null;
  const [domainsText, setDomainsText] = useState((existing?.approvedDomains ?? []).join('\n'));
  const [settings, setSettings] = useState<PortalSettings>({
    domainPurchases: existing?.domainPurchases ?? false,
    maxDomains: existing?.maxDomains ?? 1,
  });
  const [reason, setReason] = useState('');
  const domains = parseDomains(domainsText);
  const removed = (existing?.approvedDomains ?? []).filter((d) => !domains.includes(d));

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      if (editing.kind === 'revoke') {
        await api.adminRevokeCompanyEntitlement(row.companyId, key, reason.trim());
        return;
      }
      await api.adminGrantCompanyEntitlement({
        companyId: row.companyId,
        entitlement: key,
        reason: reason.trim(),
        ...(copy.domains ? { approvedDomains: domains } : {}),
        ...(copy.portalSettings ? settings : {}),
      });
    },
    onSuccess: onSaved,
    onError: (err) =>
      toast({ title: 'Could not save', description: errorText(err), variant: 'destructive' }),
  });

  // Branding held: nothing to edit, only the record and Revoke.
  const editable = editing.kind === 'grant' || (editing.kind === 'manage' && copy.domains);
  // A web grant that may buy domains needs no approved ones.
  const domainsValid =
    !copy.domains ||
    (domains.length <= MAX_DOMAINS &&
      (domains.length > 0 || (copy.portalSettings === true && settings.domainPurchases)));
  const canSave =
    reason.trim().length > 0 && !save.isPending && (editing.kind === 'revoke' || domainsValid);

  const title =
    editing.kind === 'grant'
      ? `Allow ${copy.label} for ${row.companyName}`
      : editing.kind === 'revoke'
        ? `Revoke ${copy.label} for ${row.companyName}?`
        : `${copy.label} for ${row.companyName}`;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {editing.kind === 'revoke' ? copy.revokeDescription : copy.grantDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {existing && editing.kind !== 'revoke' && (
            <p className="text-xs text-muted-foreground">
              Granted {new Date(existing.grantedAt).toLocaleDateString()} — {existing.reason}
            </p>
          )}

          {copy.domains && editable && (
            <DomainsField
              id={`partner-domains-${row.companyId}`}
              copy={copy}
              value={domainsText}
              onChange={setDomainsText}
              removed={removed}
            />
          )}

          {copy.portalSettings && editable && (
            <PortalSettingsField
              id={`partner-portal-${row.companyId}`}
              value={settings}
              onChange={setSettings}
            />
          )}

          {(editable || editing.kind === 'revoke') && (
            <ReasonField
              id={`partner-reason-${row.companyId}`}
              value={reason}
              onChange={setReason}
            />
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {editing.kind === 'manage' ? (
            <Button variant="ghost" className="text-destructive" onClick={onRevoke}>
              Revoke {copy.label}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {editable || editing.kind === 'revoke' ? 'Cancel' : 'Close'}
            </Button>
            {(editable || editing.kind === 'revoke') && (
              <Button
                variant={editing.kind === 'revoke' ? 'destructive' : 'default'}
                disabled={!canSave}
                onClick={() => save.mutate()}
              >
                {editing.kind === 'revoke' ? 'Revoke' : 'Save'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A Company not yet in the table: search it, tick what to allow, one reason.
 * Each ticked capability is still its own grant (one request, one audit
 * event each); a Company already listed is managed from its row instead.
 */
function AddCompanyDialog({
  rows,
  onClose,
  onSaved,
}: {
  rows: PartnerRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [company, setCompany] = useState<AdminCompanySearchResultDto | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<CompanyEntitlementKey[]>([]);
  // Each domain-bearing grant names its own domains: email and web approve
  // different things and are often different lists.
  const [domainsText, setDomainsText] = useState<Partial<Record<CompanyEntitlementKey, string>>>(
    {},
  );
  // The same portal-domain settings the edit dialog offers, so a buy-only Web
  // grant can be made in one step (review of #753).
  const [settings, setSettings] = useState<PortalSettings>({
    domainPurchases: false,
    maxDomains: 1,
  });
  const [reason, setReason] = useState('');
  const q = useDebouncedValue(search.trim(), 250);
  const results = useQuery({
    queryKey: queryKeys.adminCompanies.companySearch(q),
    queryFn: () => api.adminSearchCompanies(q),
    enabled: q.length >= 2 && company === null,
  });

  const held = company ? (rows.find((r) => r.companyId === company.companyId)?.grants ?? {}) : {};
  const domainsFor = (key: CompanyEntitlementKey) => parseDomains(domainsText[key] ?? '');
  const domainGrants = selected.filter((k) => GRANTS[k].domains);
  // As in the edit dialog: a grant that may buy domains needs no approved ones.
  const domainsValid = domainGrants.every((k) => {
    const n = domainsFor(k).length;
    const buyOnly = GRANTS[k].portalSettings === true && settings.domainPurchases;
    return n <= MAX_DOMAINS && (n > 0 || buyOnly);
  });

  const save = useMutation({
    mutationFn: async () => {
      // One at a time, in chip order: Web requires Branding to exist first.
      const failures: string[] = [];
      for (const key of GRANT_KEYS.filter((k) => selected.includes(k))) {
        try {
          await api.adminGrantCompanyEntitlement({
            companyId: company!.companyId,
            entitlement: key,
            reason: reason.trim(),
            ...(GRANTS[key].domains ? { approvedDomains: domainsFor(key) } : {}),
            ...(GRANTS[key].portalSettings ? settings : {}),
          });
        } catch (err) {
          failures.push(`${GRANTS[key].label}: ${errorText(err)}`);
        }
      }
      return failures;
    },
    onSuccess: (failures) => {
      // Whatever did save is real — refresh the table either way.
      if (failures.length > 0) {
        toast({
          title: 'Some grants were not saved',
          description: failures.join(' '),
          variant: 'destructive',
        });
      }
      onSaved();
    },
  });

  const canSave =
    company !== null &&
    selected.length > 0 &&
    domainsValid &&
    reason.trim().length > 0 &&
    !save.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a Company</DialogTitle>
          <DialogDescription>
            Pick what it may do under its own name. Each is a separate grant, recorded with this
            reason.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="partner-add-search">Company</Label>
            {company ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{company.name}</span>
                <span className="text-xs text-muted-foreground">
                  {company.workspaceCount} workspace{company.workspaceCount === 1 ? '' : 's'}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setCompany(null);
                    setSelected([]);
                  }}
                >
                  Change
                </Button>
              </div>
            ) : (
              <>
                <Input
                  id="partner-add-search"
                  placeholder="Search by name or Company id"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {(results.data?.companies ?? []).length > 0 && (
                  <ul className="rounded-md border border-border" role="listbox">
                    {results.data!.companies.map((c) => (
                      <li key={c.companyId}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={false}
                          className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => setCompany(c)}
                        >
                          <span>{c.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {c.workspaceCount} workspace{c.workspaceCount === 1 ? '' : 's'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {q.length >= 2 && results.data?.companies.length === 0 && (
                  <p className="text-xs text-muted-foreground">No Company matches.</p>
                )}
              </>
            )}
          </div>

          {company && (
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium">Allow</legend>
              {GRANT_KEYS.map((key) => {
                const already = !!held[key];
                const id = `partner-add-${key}`;
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={already || selected.includes(key)}
                      disabled={already}
                      onCheckedChange={(on) =>
                        setSelected((s) => (on ? [...s, key] : s.filter((k) => k !== key)))
                      }
                    />
                    <Label htmlFor={id} className="font-normal">
                      {GRANTS[key].label}
                      {already && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          — already allowed; manage it from the Company&apos;s row
                        </span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </fieldset>
          )}

          {domainGrants.map((key) => (
            <DomainsField
              key={key}
              id={`partner-add-domains-${key}`}
              copy={GRANTS[key]}
              value={domainsText[key] ?? ''}
              onChange={(v) => setDomainsText((prev) => ({ ...prev, [key]: v }))}
              removed={[]}
            />
          ))}

          {selected.some((k) => GRANTS[k].portalSettings) && (
            <PortalSettingsField id="partner-add-portal" value={settings} onChange={setSettings} />
          )}

          <ReasonField id="partner-add-reason" value={reason} onChange={setReason} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSave} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PortalSettings {
  domainPurchases: boolean;
  maxDomains: number;
}

function PortalSettingsField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: PortalSettings;
  onChange: (v: PortalSettings) => void;
}) {
  return (
    <fieldset className="grid gap-3">
      <legend className="mb-1 text-sm font-medium">Portal domains</legend>
      <label className="flex items-start gap-2 text-sm">
        <Checkbox
          checked={value.domainPurchases}
          onCheckedChange={(v) => onChange({ ...value, domainPurchases: v === true })}
          aria-label="May buy domains through Fias"
        />
        <span>
          May buy domains through Fias (charged to the Company’s payer at the registrar’s price plus
          20%)
        </span>
      </label>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-max`}>Domains it may hold</Label>
        <Input
          id={`${id}-max`}
          className="w-24"
          type="number"
          min={0}
          max={MAX_WEB_DOMAINS}
          value={value.maxDomains}
          onChange={(e) =>
            onChange({
              ...value,
              maxDomains: Math.max(0, Math.min(MAX_WEB_DOMAINS, Number(e.target.value) || 0)),
            })
          }
        />
      </div>
    </fieldset>
  );
}

function DomainsField({
  id,
  copy,
  value,
  onChange,
  removed,
}: {
  id: string;
  copy: GrantCopy;
  value: string;
  onChange: (v: string) => void;
  removed: string[];
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{copy.domainsLabel ?? 'Approved domains'}</Label>
      <Textarea
        id={id}
        placeholder={'example.com\nexample.org'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="text-xs text-muted-foreground">
        One per line, up to {MAX_DOMAINS}. Root domains, e.g. example.com.
      </p>
      {removed.length > 0 && copy.removedDomainsNote && (
        <p className="text-xs text-destructive">{copy.removedDomainsNote(removed)}</p>
      )}
    </div>
  );
}

function ReasonField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>Reason</Label>
      <Textarea id={id} maxLength={500} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
